import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ConfirmationDocument
} from 'projects/ppb-shared-library/src/lib/models/account.model';
import { Hateoas } from 'projects/ppb-shared-library/src/lib/models/hateoas.model';
import { Observable, Subject } from 'rxjs';
import { publishReplay, refCount, tap } from 'rxjs/operators';
import { AccountDetailsModel } from '../shared/models/account.model';
import { TableActions, TableQuery, tableReducer, TableStreamData } from '../shared/rxjs/table-reducer';
import { AuthTokenService } from '../shared/services/auth-token.service';
import { PpbApiService } from '../shared/services/ppb-api.service';
import { AccountRegistrationModel } from './model/account-registration.model';
import { AccountConfirmIdentityModel, AccountModel } from './models/account.model';

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  private accounts = new Subject<TableStreamData<AccountModel>>();
  private refreshAccounts = new Subject<void>();

  // tslint:disable-next-line: member-ordering
  accounts$ = this.accounts
    .pipe(
      tableReducer(),
      publishReplay(1),
      refCount()
    );

  // tslint:disable-next-line: member-ordering
  refreshAccounts$ = this.refreshAccounts;

  constructor(
    private authTokenService: AuthTokenService,
    private api: PpbApiService,
  ) {
  }

  getCurrentUserAccount(): Observable<AccountDetailsModel> {
    return this.api.get<AccountDetailsModel>('/api/v1/accounts/current');
  }

  registerAccount(account: AccountRegistrationModel): Observable<any> {
    return this.api.postPublic<any>(
      `/auth-api/v1/public/accounts`,
      account
    );
  }

  getAccountsList(query: TableQuery = this.getDefaultQuery(), filters?: string[]): void {
    const options = {
      page: (query.pageNumber - 1).toString(),
      size: query.pageLimit.toString(),
      filter: filters || '',
      sort: query.sortBy || '',
    };
    const params = new HttpParams({
      fromObject: options
    });
    this.api.get<Hateoas<AccountModel[]>>(`/api/v1/accounts/abpb=${this.authTokenService.getUserEmail()}`, params)
      .subscribe(res => {
        this.accounts.next({
          payload: res.content,
          action: TableActions[TableActions.ADD],
          query: {
            ...query,
            payloadCount: res.page.totalElements,
            sortBy: query.sortBy,
            totalPages: res.page.totalPages,
            pageNumber: res.page.number + 1
          }
        });
      });
  }

  getDefaultQuery(): TableQuery {
    return {
      pageLimit: 10,
      pageNumber: 1,
      payloadCount: 0,
      totalPages: 1
    };
  }

  sendRefreshAccountSignal(): void {
    this.refreshAccounts.next();
  }

  updateAccount(id: string, accountInfo: AccountDetailsModel): Observable<{ updateId: string }> {
    return this.api.post(`/api/v1/accounts/${id}/update`, accountInfo)
  }

  confirmUpdateAccount(id: string, updateId: string, currentPhoneSmsCode: string,
                       newPhoneSmsCode: string = null): Observable<AccountDetailsModel> {
    const payload: { currentPhoneSmsCode: string, newPhoneSmsCode?: string } = { currentPhoneSmsCode };

    if (newPhoneSmsCode) {
      payload.newPhoneSmsCode = newPhoneSmsCode;
    }

    return this.api.post(`/api/v1/accounts/${id}/update/${updateId}`, payload)
  }

  getAccount(id: string): Observable<AccountDetailsModel> {
    return this.api.get<AccountDetailsModel>(
      `/api/v1/accounts/abpb=${this.authTokenService.getUserEmail()}/${id}`,
      new HttpParams()
    );
  }

  getAccountDetails(email: string): Observable<AccountDetailsModel> {
    return this.api.get<AccountDetailsModel>(
      `/api/v1/accounts/email=${email}`,
      new HttpParams()
    );
  }

  startValidityLevelChange(id: string, ial: string,
                           documents: ConfirmationDocument[]): Observable<AccountConfirmIdentityModel> {
    return this.api.post<AccountConfirmIdentityModel>(
      `/api/v1/accounts/${id}/ial/update`,
      { ial, documents }
    );
  }

  confirmIalUpdate(id: string, updateId: string, currentPhoneSmsCode: string): Observable<AccountDetailsModel> {
    return this.api.post<AccountDetailsModel>(
      `/api/v1/accounts/${id}/ial/update/${updateId}`,
      { currentPhoneSmsCode }
    ).pipe(
      tap(_ => this.sendRefreshAccountSignal())
    );
  }
}
